import { ReactNode } from "react";
import { PatientInformationSheetContent } from "../PatientInformationSheet/content";
import { CrossList, ExternalLink, TickList } from "./blocks";
import { Faqs } from "./Faqs";
import { PurchaseCards } from "./PurchaseCards";

export interface HelpArticle {
  // Used in the url: /help/<section>/<slug>
  slug: string;
  title: string;
  // Shown on the card on the help centre index.
  summary: string;
  // Colour of the card's icon circle.
  accent: string;
  icon: ReactNode;
  // Set for links out of the portal - opens in a new window and shows the external icon.
  externalUrl?: string;
  // Article body. Compose from the blocks in ./blocks.
  body?: ReactNode;
}

const Icon = ({ children }: { children: ReactNode }) => (
  <svg
    viewBox="0 0 24 24"
    width="22"
    height="22"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
);

export interface HelpSection {
  // Used in the url: /help/<slug>
  slug: string;
  title: string;
  intro: string;
  // Name and description shown on the card on the service list at /help.
  serviceName: string;
  serviceSummary: string;
  accent: string;
  icon: ReactNode;
  articles: HelpArticle[];
}

const adhdArticles: HelpArticle[] = [
  {
    slug: "choosing-a-blood-pressure-monitor",
    title: "Choosing a Blood Pressure Monitor",
    summary: "Find out which monitors are suitable and which should be avoided.",
    accent: "#005eb8",
    icon: (
      <Icon>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </Icon>
    ),
    body: (
      <>
        <p>
          To take part in the ADHD Remote Physical Health Monitoring Pilot, you'll need a validated
          upper-arm blood pressure monitor.
        </p>

        <TickList
          title="We recommend choosing a monitor that is:"
          items={[
            "Upper-arm (rather than wrist)",
            "Clinically validated",
            "Approved by the British and Irish Hypertension Society (BIHS)",
            <>
              Displays:
              <ul>
                <li>Blood pressure</li>
                <li>Pulse (heart rate)</li>
              </ul>
            </>,
          ]}
        />

        <h2>Bluetooth is optional</h2>
        <p>You do not need Bluetooth.</p>
        <p>
          You can simply enter your readings manually into the Patient Portal.
        </p>

        <h2>Expected cost</h2>
        <p>Most suitable monitors cost £15–£25.</p>
      </>
    ),
  },
  {
    slug: "buying-a-blood-pressure-monitor",
    title: "Buying a Blood Pressure Monitor",
    summary: "Where to buy an approved monitor and what to look for.",
    accent: "#00a499",
    icon: (
      <Icon>
        <circle cx="9" cy="20" r="1.5" />
        <circle cx="18" cy="20" r="1.5" />
        <path d="M3 4h2l2.4 11h11.2l1.9-8H6" />
      </Icon>
    ),
    body: (
      <>
        <h2>Where can I buy one?</h2>
        <p>Suitable monitors are available from:</p>
        <ul className="nhsuk-list nhsuk-list--bullet">
          <li>Local pharmacies</li>
          <li>Boots</li>
          <li>Superdrug</li>
          <li>Argos</li>
          <li>Amazon</li>
          <li>Other online retailers</li>
        </ul>

        <h2>Recommended monitors</h2>
        <PurchaseCards />
      </>
    ),
  },
  {
    slug: "how-to-take-accurate-readings",
    title: "How to Take Accurate Readings",
    summary: "Step-by-step instructions before and during taking your blood pressure.",
    accent: "#7c2855",
    icon: (
      <Icon>
        <path d="M12 20s-7-4.5-7-9.5A3.5 3.5 0 0 1 12 8a3.5 3.5 0 0 1 7 2.5C19 15.5 12 20 12 20z" />
      </Icon>
    ),
    body: (
      <>
        <TickList
          title="Before you start"
          items={[
            "Sit quietly for 5 minutes",
            "Avoid caffeine, smoking and exercise for 30 minutes beforehand",
            "Avoid eating a meal for one hour beforehand",
            "Empty your bladder if needed",
            "Sit with your back supported",
            "Keep both feet flat on the floor",
            "Rest your arm on a table",
          ]}
        />

        <h2>Taking your reading</h2>
        <ol className="nhsuk-list nhsuk-list--number">
          <li>Fit the cuff to your upper arm.</li>
          <li>Press Start.</li>
          <li>Keep still and don't talk.</li>
          <li>Wait for the reading.</li>
          <li>
            Record your:
            <ul className="nhsuk-list nhsuk-list--bullet">
              <li>Systolic BP</li>
              <li>Diastolic BP</li>
              <li>Pulse</li>
            </ul>
          </li>
        </ol>

        <CrossList
          title="Common mistakes"
          items={[
            "Talking during the reading",
            "Crossing your legs",
            "Taking the reading immediately after walking upstairs",
            "Cuff over clothing",
            "Incorrect cuff position",
          ]}
        />
      </>
    ),
  },
  {
    slug: "patient-information-sheet",
    title: "Patient Information Sheet",
    summary: "Information about this service.",
    accent: "#005eb8",
    icon: (
      <Icon>
        <path d="M6 3h9l4 4v14H6z" />
        <path d="M9 9h7M9 13h7M9 17h4" />
      </Icon>
    ),
    // Read-only copy of the acceptance gate's leaflet - the help centre only displays it.
    body: <PatientInformationSheetContent />,
  },
  {
    slug: "frequently-asked-questions",
    title: "Frequently Asked Questions",
    summary: "Common questions answered.",
    accent: "#00a499",
    icon: (
      <Icon>
        <circle cx="12" cy="12" r="9" />
        <path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.4-1 .9-1 1.7" />
        <path d="M12 17h.01" />
      </Icon>
    ),
    body: <Faqs />,
  },
  {
    slug: "adhd-information-and-support",
    title: "ADHD Information and Support",
    summary: "NHS resources and support websites.",
    accent: "#006747",
    icon: (
      <Icon>
        <path d="M14 4h6v6" />
        <path d="M20 4l-8 8" />
        <path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
      </Icon>
    ),
    body: (
      <>
        <p>
          The following resources provide reliable information about ADHD, treatment,
          self-management and sources of support. These websites are provided by NHS organisations
          and will open in a new window.
        </p>

        <ul className="nhsuk-list nhsuk-list--bullet">
          <li>
            <ExternalLink href="https://www.nhsinform.scot/illnesses-and-conditions/adhd">
              ADHD Information (NHS Inform)
            </ExternalLink>
          </li>
          <li>
            <ExternalLink href="https://www.rightdecisions.scot.nhs.uk/myapp-my-mental-health/adhd">
              ADHD Self-Management and Support (Right Decisions)
            </ExternalLink>
          </li>
        </ul>
      </>
    ),
  },
];

const aedsArticles: HelpArticle[] = [
  {
    slug: "support-and-contact",
    title: "Support and Contact",
    summary: "How to get in touch with the service.",
    accent: "#7c2855",
    icon: (
      <Icon>
        <path d="M4 5h5l2 5-2.5 1.5a11 11 0 0 0 4 4L14 13l5 2v5a1 1 0 0 1-1 1A15 15 0 0 1 3 6a1 1 0 0 1 1-1z" />
      </Icon>
    ),
    body: (
      <>
        <p>
          If you have a question about your care, your appointments or the forms you have been asked
          to complete, contact the Adult Eating Disorder Service directly.
        </p>

        <div className="nhsuk-warning-callout">
          <h2 className="nhsuk-warning-callout__label">
            <span role="text">
              <span className="nhsuk-u-visually-hidden">Important: </span>
              If you need help now
            </span>
          </h2>
          <p>
            This service cannot respond to urgent problems. If you need help urgently, contact your
            GP, phone NHS 24 on <strong>111</strong>, or call <strong>999</strong> in an emergency.
          </p>
        </div>

        <h2>Information and support</h2>
        <p>
          The following resources provide reliable information about eating disorders, treatment and
          sources of support. These websites will open in a new window.
        </p>

        <ul className="nhsuk-list nhsuk-list--bullet">
          <li>
            <ExternalLink href="https://www.nhsinform.scot/illnesses-and-conditions/mental-health/eating-disorders/">
              Eating Disorders (NHS Inform)
            </ExternalLink>
          </li>
          <li>
            <ExternalLink href="https://www.beateatingdisorders.org.uk/">
              Beat - the UK's eating disorder charity
            </ExternalLink>
          </li>
        </ul>
      </>
    ),
  },
];

// ADHD and AEDS share a deployment, so the section comes from the url rather than configuration.
export const helpSections: HelpSection[] = [
  {
    slug: "adhd",
    title: "Help Centre",
    intro:
      "Find information about blood pressure monitoring, taking accurate readings and ADHD support.",
    serviceName: "Neurodevelopment Disorder Service",
    serviceSummary: "ADHD assessment and monitoring, including blood pressure monitoring.",
    accent: "#005eb8",
    icon: (
      <Icon>
        <path d="M12 4a4 4 0 0 0-4 4 3 3 0 0 0-1 5.8V16a3 3 0 0 0 3 3h2a3 3 0 0 0 3-3v-2.2A3 3 0 0 0 16 8a4 4 0 0 0-4-4z" />
        <path d="M12 4v15" />
      </Icon>
    ),
    articles: adhdArticles,
  },
  {
    slug: "aeds",
    title: "Help Centre",
    intro: "Find support for the Adult Eating Disorder Service and how to contact us.",
    serviceName: "Adult Eating Disorder Service",
    serviceSummary: "Support and contact details for the service.",
    accent: "#7c2855",
    icon: (
      <Icon>
        <path d="M12 20s-7-4.5-7-9.5A3.5 3.5 0 0 1 12 8a3.5 3.5 0 0 1 7 2.5C19 15.5 12 20 12 20z" />
      </Icon>
    ),
    articles: aedsArticles,
  },
];

export const findHelpSection = (slug?: string) =>
  helpSections.find((s) => s.slug === slug);

export const findHelpArticle = (sectionSlug?: string, articleSlug?: string) =>
  findHelpSection(sectionSlug)?.articles.find((a) => a.slug === articleSlug);

// forms/info returns the service short code; maps it to a help section for in-portal links.
export const helpSectionForService = (serviceShortCode?: string) => {
  const map: Record<string, string> = { NDD: "adhd", AEDS: "aeds" };
  const slug = map[(serviceShortCode ?? "").toUpperCase()];

  return slug && findHelpSection(slug) ? slug : undefined;
};

// Where the Help link should point. Straight to the patient's own section when we know the service,
// otherwise the service list - an anonymous visitor following an email or SMS link picks their own.
export const helpCentreLink = (serviceShortCode?: string) => {
  const section = helpSectionForService(serviceShortCode);

  return section ? `/help/${section}` : "/help";
};
